/**
 * TanStack Query, configured once.
 *
 * Query keys are centralised in `keys` so that a mutation can invalidate the
 * right things without a string literal that drifts. Add to it rather than
 * inlining an array at a call site.
 */

import { QueryClient } from '@tanstack/react-query'
import { ApiError } from './api'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retrying a 404, a 422 or a 501 wastes a round trip and delays the
      // error the user needs to see. `retryable` is the server's own verdict,
      // passed through from era-control, and is the only thing worth trusting
      // — notably a 503 from room creation IS retryable and means "no node had
      // capacity", which is a real, transient condition.
      retry: (failureCount, error) =>
        error instanceof ApiError ? error.retryable && failureCount < 3 : failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      // A tab nobody is looking at must not poll: a session detail screen left
      // open overnight would otherwise hit the control plane every few seconds
      // until morning.
      refetchIntervalInBackground: false,
    },
    mutations: { retry: false },
  },
})

export const keys = {
  health: ['health'] as const,
  rooms: ['rooms'] as const,
  room: (code: string) => ['rooms', code] as const,
  sessions: ['sessions'] as const,
  session: (sessionId: string) => ['sessions', sessionId] as const,
  transcript: (sessionId: string) => ['sessions', sessionId, 'transcript'] as const,
}
